import {
  AiDraftListItemFragment,
  AiDraftType,
  LanguageId,
} from 'lib-knowledge-management-common/graphql';

import { CategoryNameLookup } from '../hooks/useCategoryNames';

/** What a row in the suggestions list needs, resolved from an `AiDraft`. */
export type Suggestion = {
  id: string;
  type: AiDraftType;
  title: string;
  /** Full draft body as HTML. The list clamps `summary`; the preview renders this. */
  htmlContent: string;
  languageId: LanguageId;
  /**
   * The AI's own summary of the draft, or the plain-text opening of the body
   * when it wrote none — see `toSummary`.
   */
  summary: string;
  collectionId: string | undefined;
  collectionName: string | undefined;
  categoryId: string | undefined;
  categoryName: string | undefined;
  /**
   * The collections this draft may be filed into. Empty means unconstrained —
   * the queue it came from is linked to nothing, or it records no queue.
   */
  possibleCollectionIds: readonly string[];
  /**
   * The conversation the draft was written from first, then the others it drew
   * on. Only the first `isInitiating`.
   */
  conversations: readonly { csid: number; isInitiating?: boolean }[];
  initiatedBy:
    | { name: string; email: string | undefined; avatarUrl: string | undefined }
    | undefined;
  /** `modifiedAt` when the draft has been revised, else `createdAt`. */
  activityAt: number;
  forArticleId: string | undefined;
};

/**
 * Strips one level of HTML entity so extracted text reads normally.
 *
 * Named entities only — numeric ones are resolved by code point instead, so the
 * table does not have to list them in both notations.
 */
const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&nbsp;': ' ',
};

/**
 * One entity as text.
 *
 * Numeric entities are decoded in both notations: serializers emit an
 * apostrophe as `&#39;` or `&#x27;` about equally, and the hex form is not
 * something to leave sitting in the summary as literal text.
 *
 * An entity that is neither is left as it was: replacing the unknown with a
 * space silently eats the character, and the entity is at least recognisable.
 */
function decodeEntity(entity: string): string {
  const named = ENTITIES[entity.toLowerCase()];
  if (named) return named;

  const numeric = /^&#(x)?([0-9a-f]+);$/i.exec(entity);
  if (numeric) {
    const codePoint = parseInt(numeric[2], numeric[1] ? 16 : 10);
    // Surrogates and out-of-range values would throw.
    if (
      Number.isFinite(codePoint) &&
      codePoint > 0 &&
      codePoint <= 0x10ffff &&
      !(codePoint >= 0xd800 && codePoint <= 0xdfff)
    ) {
      return String.fromCodePoint(codePoint);
    }
  }

  return entity;
}

/**
 * A short description of a draft that has no `summary` of its own. Takes the
 * opening of the body as plain text and lets the row clamp it to two lines.
 *
 * Deliberately not a parser: this runs per row, the input is the AI's own
 * generated markup, and the result is truncated anyway.
 */
export function toSummary(htmlContent: string, maxLength = 200): string {
  const text = htmlContent
    // Treat block boundaries as spaces so words don't run together.
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&[a-z]+;|&#x?[0-9a-f]+;/gi, decodeEntity)
    .replace(/\s+/g, ' ')
    .trim();

  if (text.length <= maxLength) return text;
  // Cut on a word boundary so the clamp doesn't land mid-word.
  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : maxLength)}…`;
}

export function toSuggestion(
  draft: AiDraftListItemFragment,
  collectionNamesById: ReadonlyMap<string, string>,
  categoryName: CategoryNameLookup,
): Suggestion {
  return {
    id: draft.id,
    type: draft.type,
    title: draft.title,
    htmlContent: draft.htmlContent,
    languageId: draft.languageId,
    // The AI does not always write a summary, and the opening of the body is
    // the next best description of the draft. `trim()` rather than `??` because
    // whitespace reads as an empty row, and `NonEmptyString` does not rule it
    // out.
    summary: draft.summary?.trim() || toSummary(draft.htmlContent),
    collectionId: draft.collectionId ?? undefined,
    collectionName: draft.collectionId
      ? collectionNamesById.get(draft.collectionId)
      : undefined,
    categoryId: draft.categoryId ?? undefined,
    categoryName: categoryName(draft.categoryId ?? undefined, draft.languageId),
    possibleCollectionIds: draft.possibleCollectionIds,
    // `referencedConversationIds` is documented as the *other* conversations, so
    // the one that triggered the draft has to be added — but it does sometimes
    // appear among them too, and listing it twice would both duplicate a row and
    // overstate the count.
    conversations: [
      { csid: draft.initiatedFromConversation, isInitiating: true },
      ...draft.referencedConversationIds
        .filter((csid) => csid !== draft.initiatedFromConversation)
        .map((csid) => ({ csid })),
    ],
    initiatedBy: draft.initiatedBy?.name
      ? {
          name: draft.initiatedBy.name,
          email: draft.initiatedBy.email ?? undefined,
          avatarUrl: draft.initiatedBy.avatarUrl ?? undefined,
        }
      : undefined,
    activityAt: draft.modifiedAt ?? draft.createdAt,
    forArticleId: draft.forArticleId ?? undefined,
  };
}
